import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { kycRoute, kycStepsDone, normalizeKyc } from '../../lib/kyc';

const COPY = {
  NOT_STARTED: ['Complete your KYC verification', 'KYC verification is required before live payouts.', 'Start KYC'],
  DRAFT: ['KYC application in progress', 'Finish uploading your documents and submit for review.', 'Continue KYC'],
  SUBMITTED: ['KYC submitted · under review', 'Our verification team is reviewing your documents.', 'View Status'],
  UNDER_REVIEW: ['KYC submitted · under review', 'Our verification team is reviewing your documents.', 'View Status'],
  REJECTED: ['KYC application rejected', 'Review the remarks and resubmit your application.', 'Update & Resubmit'],
  REUPLOAD_REQUIRED: ['Action required on your KYC', 'Our team has requested updated documents.', 'Upload Documents'],
};

// Horizontal onboarding banner — hidden once KYC is approved. State comes from GET /merchants/me/kyc/status.
export const OnboardingBanner = ({ testIdPrefix = 'onboarding' }) => {
  const { api, merchant } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!merchant || merchant.kyc_status === 'approved') return;
    api.get('/merchants/me/kyc/status').then(r => setState(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant?.kyc_status]);

  if (!merchant || merchant.kyc_status === 'approved') return null;
  const status = normalizeKyc(state?.status || merchant.kyc_status);
  const [title, sub, cta] = COPY[status] || COPY.NOT_STARTED;
  const done = state ? kycStepsDone(state.steps) : 1;
  const step = (label, isDone, active) => (
    <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
      <span className={`w-4 h-4 rounded-full flex items-center justify-center ${isDone ? 'bg-green-500 text-white' : active ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'}`}>
        {isDone ? <CheckCircle2 className="w-3 h-3" /> : active ? '●' : ''}
      </span>
      <span className={isDone ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </div>
  );
  return (
    <Card className="border-amber-500/30 bg-amber-500/5" data-testid={`${testIdPrefix}-banner`}>
      <CardContent className="py-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium" data-testid={`${testIdPrefix}-banner-title`}>
                {title} <span className="text-xs text-muted-foreground">· {done} of 3 steps completed</span>
              </p>
              <p className="text-sm text-muted-foreground">{sub}</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            {step('Business Details', done >= 1, done === 0)}
            {step('KYC Documents', done >= 2, done === 1)}
            {step('Verification', done >= 3, done === 2)}
          </div>
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm shrink-0"
            onClick={() => navigate(kycRoute(status))} data-testid="complete-kyc-btn">
            {cta} <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
